import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import EstatePage from "@/components/sites/EstatePage";
import Reveal from "@/components/sites/Reveal";
import { useBrand, useTenantPath, useTenantSlug } from "@/lib/brand";
import { api } from "@/lib/api";
import { withPreview } from "@/lib/sitePreview";
import { agencyDate } from "@/lib/manageFormat";

/**
 * The guides index and one guide, in the Estate theme.
 *
 * Both live here because the list is the article's own header repeated:
 * title, excerpt, date. Articles come from `/crm/public/guides`, which is
 * the agency's own writing — nothing is generated and an agency with no
 * guides gets a page that says so rather than a grid of placeholders.
 *
 * The article itself reuses the `.article` + `.toc` layout the area guide
 * already uses, which is the pack's own long-read shape.
 */
export function EstateGuides({ preview = true }) {
  const brand = useBrand();
  const biz = brand.business || {};
  const tenantPath = useTenantPath();
  const { slug: tenantSlug, known } = useTenantSlug();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const p = (href) => withPreview(href, preview);

  useEffect(() => {
    if (!known) return;
    api.get(`/crm/public/guides?tenant=${tenantSlug}`)
      .then(({ data }) => setItems(data.items || data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tenantSlug, known]);

  return (
    <EstatePage
      preview={preview}
      title={biz.name ? `Guides and advice, ${biz.name}` : undefined}
      description={`Moving, selling and letting in ${brand.regions_covered_prose || "our area"}, written by ${biz.name || "us"}.`}
      eyebrow="Guides and advice"
      heading="Guides"
      lede={brand.regions_covered_prose
        ? `Moving, selling and letting in ${brand.regions_covered_prose}.` : ""}
      crumbs={[{ label: "Guides" }]}
    >
      {loading ? (
        <p className="note">Loading…</p>
      ) : items.length === 0 ? (
        <p className="lede">Nothing published here yet.</p>
      ) : (
        <div className="grid cols-3">
          {items.map((item) => (
            <Reveal key={item.slug}>
              <Link className="pcard" to={p(tenantPath(`guides/${item.slug}`))}>
                <div className="pcard-body">
                  {item.category && <div className="eyebrow plain" style={{ fontSize: 10 }}>{item.category}</div>}
                  <div className="addr" style={{ fontSize: 22, marginTop: 8 }}>{item.title}</div>
                  {item.excerpt && <div className="loc" style={{ marginTop: 8 }}>{item.excerpt}</div>}
                  {item.updated_at && (
                    <div className="note" style={{ marginTop: 10 }}>{agencyDate(item.updated_at)}</div>
                  )}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      )}
    </EstatePage>
  );
}

export function EstateGuide({ preview = true }) {
  const { guideSlug } = useParams();
  const brand = useBrand();
  const biz = brand.business || {};
  const tenantPath = useTenantPath();
  const { slug: tenantSlug, known } = useTenantSlug();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!known) return;
    api.get(`/crm/public/guides/${guideSlug}?tenant=${tenantSlug}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setError(true));
  }, [guideSlug, tenantSlug, known]);

  if (error) {
    return (
      <EstatePage preview={preview} heading="That guide is not here" cta={false}
                  crumbs={[{ label: "Guides", to: tenantPath("guides") }, { label: "Not found" }]}>
        <p className="lede">It may have been renamed or taken down.</p>
      </EstatePage>
    );
  }
  if (!article) {
    return <EstatePage preview={preview} heading="" cta={false}><p className="note">Loading…</p></EstatePage>;
  }

  // The API returns one `body` string of double-newline paragraphs and no
  // headings, so there is nothing to build a table of contents from. A
  // sidebar listing nothing is worse than no sidebar.
  const paragraphs = String(article.body || "").split("\n\n").map((x) => x.trim()).filter(Boolean);

  return (
    <EstatePage
      preview={preview}
      title={`${article.title}, ${biz.name || ""}`.trim().replace(/,$/, "")}
      description={article.excerpt || undefined}
      eyebrow="Guide"
      heading={article.title}
      lede={article.excerpt || ""}
      crumbs={[{ label: "Guides", to: tenantPath("guides") }, { label: article.title }]}
      jsonLd={{
        "@context": "https://schema.org", "@type": "Article",
        headline: article.title, description: article.excerpt,
        datePublished: article.created_at, dateModified: article.updated_at,
        author: biz.name ? { "@type": "Organization", name: biz.name } : undefined,
        publisher: biz.name ? { "@type": "Organization", name: biz.name } : undefined,
      }}
    >
      <div className="prose">
        {paragraphs.map((para, i) => <p key={i}>{para}</p>)}
      </div>
    </EstatePage>
  );
}

